/**
 * Token utility functions for managing access and refresh tokens.
 * Tokens are persisted in localStorage so sessions survive page refresh.
 * sessionStorage is kept as a secondary cache for same-tab access.
 *
 * No JWT decode / expiry checks on the FE — backend decides via 401/403.
 */

const ACCESS_TOKEN_KEY = "token";
const REFRESH_TOKEN_KEY = "refreshToken";
const USER_ID_KEY = "Id";
const USER_EMAIL_KEY = "email";
const USER_NAME_KEY = "Name";

/**
 * Store tokens and user data
 */
export const storeTokens = (tokenData) => {
  const accessToken = tokenData.accessToken || tokenData.token;
  if (accessToken) {
    localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
    sessionStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
  }

  const refreshToken = tokenData.refreshToken || tokenData.refresh_token;
  if (refreshToken) {
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    sessionStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  }

  if (tokenData.id) {
    localStorage.setItem(USER_ID_KEY, tokenData.id);
    sessionStorage.setItem(USER_ID_KEY, tokenData.id);
  }

  if (tokenData.email) {
    localStorage.setItem(USER_EMAIL_KEY, tokenData.email);
    sessionStorage.setItem(USER_EMAIL_KEY, tokenData.email);
  }

  if (tokenData.name) {
    localStorage.setItem(USER_NAME_KEY, tokenData.name);
    sessionStorage.setItem(USER_NAME_KEY, tokenData.name);
  }

  if (tokenData.otp) {
    localStorage.setItem("Otp", tokenData.otp);
  }
};

export const getAccessToken = () => {
  const token =
    localStorage.getItem(ACCESS_TOKEN_KEY) ||
    sessionStorage.getItem(ACCESS_TOKEN_KEY);

  // Treat empty / junk values as logged out
  if (
    !token ||
    token === "null" ||
    token === "undefined" ||
    !String(token).trim()
  ) {
    return null;
  }
  return token;
};

export const getRefreshToken = () => {
  return (
    localStorage.getItem(REFRESH_TOKEN_KEY) ||
    sessionStorage.getItem(REFRESH_TOKEN_KEY)
  );
};

export const clearTokens = () => {
  sessionStorage.removeItem(ACCESS_TOKEN_KEY);
  sessionStorage.removeItem(REFRESH_TOKEN_KEY);
  sessionStorage.removeItem(USER_ID_KEY);
  sessionStorage.removeItem(USER_EMAIL_KEY);
  sessionStorage.removeItem(USER_NAME_KEY);

  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(USER_ID_KEY);
  localStorage.removeItem(USER_EMAIL_KEY);
  localStorage.removeItem(USER_NAME_KEY);
};

/**
 * Route guard: requires an access token.
 * refreshToken alone must NOT unlock pages — it is only used by the API interceptor.
 */
export const isAuthenticated = () => {
  return !!getAccessToken();
};

export const redirectToLogin = () => {
  clearTokens();
  window.location.href = "/";
};

export const performLogout = async (logoutApiCall) => {
  try {
    const refreshToken = getRefreshToken();

    if (logoutApiCall) {
      try {
        await logoutApiCall({ refreshToken });
      } catch (apiError) {
        console.warn(
          "Logout API call failed, but clearing local tokens anyway:",
          apiError,
        );
      }
    }
  } catch (error) {
    console.warn("Error during logout API call:", error);
  } finally {
    clearTokens();

    const theme = localStorage.getItem("theme");
    localStorage.clear();
    if (theme) {
      localStorage.setItem("theme", theme);
    }

    window.location.href = "/";
  }
};
